import React, { createContext, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { useLocation, useNavigate } from "react-router";
import useAuth from "src/hooks/useAuth";

const OrdersContext = createContext(null);

export const OrdersProvider = ({ queryKey, children, query, limit = 20, variables = {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const wizardMode = user.company.settings.wizard_mode;
  const simpleCheckoutForContractWarehouse = user.company.settings.simple_checkout_for_contract_warehouse;
  const isExtendedCheckout = wizardMode || simpleCheckoutForContractWarehouse;
  const searchParams = new URLSearchParams(location.search);

  const type = useMemo(() => {
    if (location.pathname.includes("reserved") || location.pathname.includes("reservations")) {
      return "reservations";
    }
    if (location.pathname.includes("outstanding_orders")) {
      return "outstanding_orders";
    }
    return "orders";
  }, [location.pathname]);

  const search = searchParams.get("q") || "";
  const page = parseInt(searchParams.get("page")) || 1;
  const sortParam = searchParams.get("sort");
  const [sortField, sortDirection] = sortParam ? sortParam.split(":") : [null, null];

  const sortBy = useMemo(() => {
    if (sortField && sortDirection) {
      return [{ field: sortField, sortDirection }];
    }
    if (location.pathname.includes("outstanding_orders")) {
      return isExtendedCheckout
        ? { field: "start_date", sortDirection: "asc" }
        : { field: "delivery_date", sortDirection: "desc" };
    }
    return { field: "created_at", sortDirection: "desc" };
  }, [isExtendedCheckout, location.pathname, sortField, sortDirection]);

  const { sortField: workOrdersSortField, sortDirection: workOrdersSortDirection } = useMemo(() => {
    const mapField = field => {
      switch (field) {
        case "delivery_date":
        case "end_date":
          return "end_date";
        case "requested_at":
        case "created_at":
          return "created_at";
        case "work_order_number":
          return "work_order_number";
        case "id":
          return "id";
        default:
          return "created_at";
      }
    };

    if (sortField && sortDirection) {
      return { sortField: mapField(sortField), sortDirection };
    }

    if (location.pathname.includes("outstanding_orders")) {
      return { sortField: "end_date", sortDirection: "asc" };
    }

    return { sortField: "created_at", sortDirection: "desc" };
  }, [sortField, sortDirection, location.pathname]);

  // Prepare variables based on query type
  const queryVariables = useMemo(() => {
    const baseVariables = {
      type,
      search,
      limit,
      page,
      ...variables,
    };

    // WorkOrders query uses sortField/sortDirection, others use sortBy
    if (queryKey === 'workOrders') {
      return {
        ...baseVariables,
        sortField: workOrdersSortField,
        sortDirection: workOrdersSortDirection,
      };
    } else {
      return {
        ...baseVariables,
        sortBy,
      };
    }
  }, [type, search, limit, page, variables.orderType, variables.warehouseId, variables.clientId, variables.projectId, variables.statusFilter, queryKey, workOrdersSortField, workOrdersSortDirection, sortBy]);

  const { data: { [queryKey]: { collection = [], metadata = {} } = {} } = {}, loading, networkStatus, ...rest } = useQuery(
    query,
    {
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      variables: queryVariables,
    }
  );

  const sort = useMemo(() => ({ column: sortField, order: sortDirection }), [
    sortField,
    sortDirection,
  ]);

  const onChangePage = useCallback(
    page => {
      const searchParams = new URLSearchParams(location.search);
      if (page > 1) {
        searchParams.set("page", page);
      } else {
        searchParams.delete("page");
      }
      navigate({ search: searchParams.toString() });
    },
    [navigate, location.search]
  );

  const onSort = useCallback(
    column => {
      const newSearchParams = new URLSearchParams(location.search);
      const newSort =
        sort?.column === column
          ? { field: column, sortDirection: sort.order === "asc" ? "desc" : "asc" }
          : { field: column, sortDirection: "asc" };

      newSearchParams.set("sort", `${newSort.field}:${newSort.sortDirection}`);
      navigate({ search: newSearchParams.toString() });
    },
    [navigate, location.search, sort]
  );

  const value = {
    ...rest,
    loading,
    networkStatus,
    collection,
    metadata,
    sort,
    onChangePage,
    onSort,
  };

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};

export const useOrders = () => {
  const context = React.useContext(OrdersContext);
  if (!context) {
    throw new Error("useOrders must be used within a OrdersProvider");
  }
  return context;
};
